import { isContractEmailFailed } from "app/containers/JobCalendar/utils/sendFeedback";
import { SYSTEM_TEMPLATE_ID } from "app/containers/ContractManagement/templateHelpers";

// Pure helpers for the Job Setup "Save & Send" composite call
// (POST /jobs/{id}/setup-send/). The endpoint always answers 200 and reports
// each requested track on its own, so the verdict is read per track — never
// from the HTTP status. Kept free of React so they are unit-testable.

// A failed contract_email means BoldSign accepted the signature request and
// only Morph's copy-of-the-contract email did not reach the client: a warning,
// not a send failure.
const toTrackOutcome = (track) =>
  track?.ok
    ? { ok: true, emailSkipped: isContractEmailFailed(track.contract_email) }
    : { ok: false, message: track?.error?.user_error_msg };

/**
 * Map a setup-send response to the { contract, payment } shape
 * buildSendMessages reads. Only requested tracks appear; a requested track
 * missing from the response reads as failed.
 */
export const toSendOutcome = (res, { sendContract, sendPayment }) => ({
  ...(sendContract && { contract: toTrackOutcome(res?.contract) }),
  ...(sendPayment && { payment: toTrackOutcome(res?.payment_request) }),
});

/**
 * The outcome when the request itself failed (network, 404): every requested
 * track failed, with no backend message to show.
 */
export const failedSendOutcome = ({ sendContract, sendPayment }) => ({
  ...(sendContract && { contract: { ok: false } }),
  ...(sendPayment && { payment: { ok: false } }),
});

const NO_TRACK_STATES = { contractState: null, paymentState: null };

/**
 * The contract / payment state Job Setup gates its checkboxes on, taken from
 * the jobs slice's detail. Null while that detail is loading or belongs to a
 * different job, so the modal hides the tracks rather than offering a send
 * off another job's (or a stale) status.
 */
export const pickTrackStates = ({ detail, jobId, status }) => {
  if (!jobId || !detail || status !== "success") return NO_TRACK_STATES;
  if (String(detail.id) !== String(jobId)) return NO_TRACK_STATES;
  return {
    contractState: { status: detail.contract?.status },
    paymentState: { status: detail.payment?.status },
  };
};

// The picked contract template rides on the contract track only. The System
// Built-in has no UUID, so it travels as use_system_template; omitting both
// lets the backend fall back to the inspector's default template.
export const toSetupSendBody = ({
  sendContract = false,
  sendPaymentRequest = false,
  contractTemplateId,
}) => ({
  send_contract: sendContract,
  send_payment_request: sendPaymentRequest,
  ...(sendContract &&
    contractTemplateId &&
    (contractTemplateId === SYSTEM_TEMPLATE_ID
      ? { use_system_template: true }
      : { contract_template_id: contractTemplateId })),
});
